import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { PartnerUpdatesAdmin, PARTNERS_CHANGED } from "@/components/admin/partners/PartnerUpdatesAdmin";
import { PartnerThreadsAdmin } from "@/components/admin/partners/PartnerThreadsAdmin";
import { PartnerControl } from "@/components/admin/partners/PartnerControl";
import { PartnersManager } from "@/components/admin/partners/PartnersManager";

const TABS = [
    { key: "atualizacoes", label: "Atualizações pendentes", count: (s) => s.pending_updates },
    { key: "questoes", label: "Questões da obra", count: (s) => s.unread_threads },
    { key: "controlo", label: "Controlo diário", count: (s) => s.missing_days, danger: true },
    { key: "parceiros", label: "Parceiros" },
];

export default function AdminParceiros() {
    const [params, setParams] = useSearchParams();
    const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "atualizacoes";
    const [summary, setSummary] = useState({});

    const loadSummary = useCallback(() => {
        api.get("/admin/partner-summary").then(({ data }) => setSummary(data)).catch(() => {});
    }, []);
    useEffect(() => {
        loadSummary();
        window.addEventListener(PARTNERS_CHANGED, loadSummary);
        return () => window.removeEventListener(PARTNERS_CHANGED, loadSummary);
    }, [loadSummary]);

    const go = (key) => setParams({ tab: key }, { replace: true });
    return (
        <div data-testid="admin-partners-page">
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[hsl(var(--forest))]">Parceiros</h1>
            <p className="text-stone-500 mt-1 mb-6 text-sm">Atualizações e questões dos parceiros. Nada chega ao cliente sem a sua aprovação.</p>
            {summary.urgent_open > 0 && (
                <button type="button" onClick={() => go("questoes")} className="w-full mb-5 flex items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-left text-sm text-red-900" data-testid="partners-urgent-banner">
                    <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                    <span><strong>{summary.urgent_open === 1 ? "1 questão urgente em aberto" : `${summary.urgent_open} questões urgentes em aberto`}</strong> · segurança, danos ou impedimentos à execução.</span>
                </button>
            )}
            <div className="flex gap-2 overflow-x-auto pb-1 mb-6" role="tablist">
                {TABS.map((t) => {
                    const n = t.count ? t.count(summary) || 0 : 0;
                    return (
                        <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => go(t.key)} data-testid={`partners-tab-${t.key}`}
                            className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-[hsl(var(--copper))] text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-300"}`}>
                            {t.label}
                            {n > 0 && <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${tab === t.key ? "bg-white text-[hsl(var(--copper))]" : t.danger ? "bg-red-600 text-white" : "bg-[hsl(var(--forest))] text-white"}`} data-testid={`partners-tab-count-${t.key}`}>{n}</span>}
                        </button>
                    );
                })}
            </div>
            {tab === "atualizacoes" && <PartnerUpdatesAdmin />}
            {tab === "questoes" && <PartnerThreadsAdmin />}
            {tab === "controlo" && <PartnerControl />}
            {tab === "parceiros" && <PartnersManager />}
        </div>
    );
}
